import { Link, useNavigate } from '@tanstack/react-router';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import { releaseSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import type { StoreProduct } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@nucleus-commerce/ui/components/dropdown-menu';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import { ViewOnStorefrontButton } from '@/features/stores';

import { displayTitle } from '../api/mappers';
import { useAdjacentStoreProducts } from '../hooks/use-adjacent-store-products';
import type { OtherStore } from '../hooks/use-other-stores';
import { storeProductLiveState } from '../schemas/live-state';

import { RemoveStoreProductDialog } from './remove-store-product-dialog';
import { StoreLiveChip } from './store-live-chip';

function MenuLine({ title, hint }: { title: string; hint?: string }) {
  return (
    <span className="flex flex-col gap-px">
      <span className="font-medium">{title}</span>
      {hint ? <span className="text-muted-foreground text-xs">{hint}</span> : null}
    </span>
  );
}

function StoreProductMoreActions({ storeId, row }: { storeId: string; row: StoreProduct }) {
  const { t } = useTranslation('storeCatalog');
  const { can } = usePermissions();
  const navigate = useNavigate();
  const [removing, setRemoving] = useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(row.storefrontUrl ?? window.location.href);
      toast.success(t('storePage.more.linkCopied'));
    } catch {
      toast.error(t('storePage.more.linkNotCopied'));
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" data-tour="store-catalog.product.master-link">
            {t('storePage.more.label')}
            <ChevronDown aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72">
          <DropdownMenuItem asChild>
            <Link to="/org/products/$productId" params={{ productId: row.productId }}>
              <MenuLine title={t('master.open')} hint={t('storePage.more.openMasterHint')} />
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => void copyLink()}>
            <MenuLine title={t('storePage.more.copyLink')} />
          </DropdownMenuItem>
          {can('audit.read') ? (
            <DropdownMenuItem asChild>
              <Link
                to="/org/activity"
                search={{ entityType: 'store_product', entityId: row.id, storeId }}
              >
                <MenuLine
                  title={t('storePage.more.history')}
                  hint={t('storePage.more.historyHint')}
                />
              </Link>
            </DropdownMenuItem>
          ) : null}
          {can('products.write') ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onSelect={() => setRemoving(true)}
              >
                <MenuLine
                  title={t('storePage.more.remove')}
                  hint={t('storePage.more.removeHint')}
                />
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      <RemoveStoreProductDialog
        storeId={storeId}
        row={removing ? row : null}
        onOpenChange={(open) => {
          if (!open) setRemoving(false);
        }}
        onRemoved={() => {
          // The record is gone; a dirty form must not keep us on its page.
          releaseSaveBar();
          void navigate({ to: '/s/$storeId/catalog', params: { storeId } });
        }}
      />
    </>
  );
}

function StepLink({
  storeId,
  target,
  label,
  children,
}: {
  storeId: string;
  target: string | null;
  label: string;
  children: ReactNode;
}) {
  if (!target) {
    return (
      <Button type="button" variant="outline" size="icon-sm" aria-label={label} disabled>
        {children}
      </Button>
    );
  }
  return (
    <Button asChild variant="outline" size="icon-sm">
      <Link
        to="/s/$storeId/catalog/$storeProductId"
        params={{ storeId, storeProductId: target }}
        aria-label={label}
      >
        {children}
      </Link>
    </Button>
  );
}

export interface StoreProductHeaderProps {
  storeId: string;
  row: StoreProduct;
  others: readonly OtherStore[];
  /** The master's product type; null without one or until it loads. */
  productTypeName: string | null;
}

/** Title and what shoppers get now, where else it's sold, and the page's rare actions. */
export function StoreProductHeader({
  storeId,
  row,
  others,
  productTypeName,
}: StoreProductHeaderProps) {
  const { t } = useTranslation('storeCatalog');
  const format = useFormat();
  const adjacent = useAdjacentStoreProducts(storeId, row.id);
  const state = storeProductLiveState({
    status: row.status,
    masterStatus: row.product.status,
    publishAt: row.publishAt,
  });
  const alsoIn =
    others.length > 0
      ? t('storePage.header.alsoIn', {
          stores: new Intl.ListFormat(format.locale, { type: 'conjunction' }).format(
            others.map(({ store }) => store.name),
          ),
        })
      : null;
  const line = [row.product.skuRoot, productTypeName, alsoIn].filter(Boolean).join(' · ');

  return (
    <PageHeader
      title={displayTitle(row)}
      meta={<StoreLiveChip state={state} since={row.publishedAt} />}
      description={line || undefined}
      backTo={{ to: '/s/$storeId/catalog', params: { storeId } }}
      backLabel={t('title')}
      actions={
        <>
          <ViewOnStorefrontButton url={row.storefrontUrl} live={state.kind === 'live'} />
          <StoreProductMoreActions storeId={storeId} row={row} />
          {adjacent ? (
            <span className="flex gap-1">
              <StepLink
                storeId={storeId}
                target={adjacent.previous}
                label={t('storePage.header.previous')}
              >
                <ChevronLeft aria-hidden />
              </StepLink>
              <StepLink storeId={storeId} target={adjacent.next} label={t('storePage.header.next')}>
                <ChevronRight aria-hidden />
              </StepLink>
            </span>
          ) : null}
        </>
      }
    />
  );
}
